import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface NuevoProyectoDocData {
  /** Nombres de los archivos que se están adjuntando (para mostrarlos). */
  fileNames: string[];
  /** Nombre sugerido del proyecto (archivo sin extensión). */
  nombreSugerido: string;
  /** @deprecated los proyectos son siempre de Invering; se ignora. */
  empresaFija?: string | null;
}

export interface NuevoProyectoDocResult {
  nombre: string;
  /** Siempre 'invering': los contratos de obra son de Invering. */
  empresa: 'invering';
}

/**
 * Se abre cuando el usuario adjunta un documento desde el chat estando FUERA de
 * un proyecto. Pide lo único que el backend no puede adivinar del PDF: el nombre
 * interno del proyecto. Con eso, el asistente crea el proyecto y sube el
 * documento en una sola operación.
 *
 * La empresa NO se pregunta: los proyectos/contratos de obra son siempre de
 * Invering (Infinitus es inmobiliaria y no maneja proyectos).
 */
@Component({
  selector: 'inv-nuevo-proyecto-doc-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  template: `
    <div mat-dialog-title class="flex items-center gap-2">
      <mat-icon class="text-primary-600">auto_awesome</mat-icon>
      <span>Nuevo proyecto desde el documento</span>
    </div>

    <mat-dialog-content>
      <div class="mb-4 p-2 rounded bg-gray-50 border border-gray-200 max-h-32 overflow-y-auto">
        <div *ngFor="let f of data.fileNames"
             class="flex items-center gap-2 text-sm text-secondary py-0.5">
          <mat-icon class="!text-base flex-shrink-0">description</mat-icon>
          <span class="truncate">{{ f }}</span>
        </div>
      </div>

      <p class="text-sm text-secondary mb-3">
        La IA va a leer
        {{ data.fileNames.length === 1 ? 'el documento' : 'los ' + data.fileNames.length + ' documentos' }}
        y completar el contrato, las pólizas, la obra y el cliente. Solo necesito
        el nombre del proyecto, que no se puede deducir de los archivos:
      </p>

      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Nombre del proyecto</mat-label>
        <input matInput [formControl]="nombreCtrl" autocomplete="off" />
        <mat-hint>Cómo lo conocen internamente los empleados.</mat-hint>
        <mat-error *ngIf="nombreCtrl.invalid">El nombre es requerido.</mat-error>
      </mat-form-field>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Cancelar</button>
      <button
        mat-flat-button
        color="primary"
        [disabled]="nombreCtrl.invalid"
        (click)="confirmar()"
      >
        <mat-icon class="mr-1">auto_awesome</mat-icon>
        Crear y procesar
      </button>
    </mat-dialog-actions>
  `,
})
export class NuevoProyectoDocDialogComponent {
  readonly dialogRef = inject(MatDialogRef<NuevoProyectoDocDialogComponent>);
  readonly data: NuevoProyectoDocData = inject(MAT_DIALOG_DATA);

  readonly nombreCtrl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  constructor() {
    this.nombreCtrl.setValue(this.data.nombreSugerido);
  }

  confirmar(): void {
    if (this.nombreCtrl.invalid) return;
    const result: NuevoProyectoDocResult = {
      nombre: this.nombreCtrl.value.trim(),
      empresa: 'invering',
    };
    this.dialogRef.close(result);
  }
}
